import React, { useState, useEffect } from "react";

import HeaderTemplate from "../templates/HeaderTemplate";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import {
  MissionConditions,
  Mission,
  ParsedMission,
  MissionDisplayDetail,
  MissionsMissionidPutRequestBody,
} from "../common/type/type";
import env from "../common/config/env";

import ActionDialogButton from "../atoms/ActionDialogButton";
import { getMissionDetail, editMission } from "../../services/missionApi";
import { MissionFormBase } from "../molecules/MissionFormBase";

function parseMockMission(raw: Mission): ParsedMission {
  return {
    id: raw.id, // 必要に応じて補完
    title: raw.title,
    conditions: JSON.parse(raw.conditions) as MissionConditions,
    point: raw.point,
    startedAt: raw.startedAt,
    endedAt: raw.endedAt,
    deletedAt: "", // 必要に応じて補完
    createdAt: "", // 必要に応じて補完
    updatedAt: "", // 必要に応じて補完
    missionType: {
      id: raw.missionType.id,
      name: raw.missionType.name, // 補完
      text: raw.missionType.text, // 補完
    },
    missionDisplay: {
      missionDisplayTypeId: raw.missionDisplay.missionDisplayType.id,
      detail: JSON.parse(raw.missionDisplay.detail) as MissionDisplayDetail,
    },
  };
}

function toPutRequestBody(
  parsed: ParsedMission
): MissionsMissionidPutRequestBody {
  return {
    missionTypeId: parsed.missionType.id,
    title: parsed.title,
    conditions: JSON.stringify(parsed.conditions),
    point: parsed.point,
    startedAt: parsed.startedAt,
    endedAt: parsed.endedAt,
    missionDisplay: {
      missionDisplayTypeId: parsed.missionDisplay.missionDisplayTypeId, // 注意: IDの意味が異なる可能性あり
      detail: JSON.stringify(parsed.missionDisplay.detail),
    },
  };
}

// missionTypeIdごとにconditionsを整形する関数
function adjustConditionsByMissionType(
  editData: MissionsMissionidPutRequestBody
) {
  const { missionTypeId, conditions } = editData;
  if (
    missionTypeId === "019372c6-c935-72c6-ab12-e352aba76fac" ||
    missionTypeId === "019372c6-c935-732d-b4cc-889b56cd7857"
  ) {
    // 電気代シミュレーションまたは記事閲覧はconditions自体をnull
    return null;
  } else if (missionTypeId === "019372c6-c935-7261-8955-f55c1b7d9ff2") {
    // アプリ起動: targetYearMonthのみ
    const current = JSON.parse(conditions);
    return JSON.stringify({ targetYearMonth: current.targetYearMonth });
  }
  return conditions;
}

// missionDisplayTypeIdごとにmissionDisplay.detailを整形する関数
function adjustMissionDisplayDetail(editData: MissionsMissionidPutRequestBody) {
  const { missionDisplay } = editData;
  const detailObj = JSON.parse(missionDisplay.detail);
  if (
    missionDisplay.missionDisplayTypeId ===
    "019372c6-c935-7000-9eef-4043e704ad85"
  ) {
    // WebView: urlのみ
    return JSON.stringify({ url: detailObj.url || "" });
  }
  return missionDisplay.detail;
}

export default function MissionEdit() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [editData, setEditData] = useState<MissionsMissionidPutRequestBody>();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [data, setData] = useState<Mission>();
  const [error, setError] = useState(null);

  /**
   * URLのパスパラメータにあるadvertisement_idを格納する変数
   */
  const paramsMissionId: string = String(useParams().mission_id);

  console.log(paramsMissionId);

  useEffect(() => {
    const fetchMission = async () => {
      const result = await getMissionDetail(paramsMissionId);
      if (result) {
        setData(result);
      } else {
        setError(
          "データ取得に失敗しました。ログインし直すか、設計にご連絡ください。"
        );
      }
    };
    fetchMission();
  }, [paramsMissionId]);

  useEffect(() => {
    if (data) {
      const parsedMission = parseMockMission(data);
      setEditData(toPutRequestBody(parsedMission));
    }
  }, [data]);

  if (error) return <p>{error}</p>;
  if (!data) return <p>読み込み中...</p>;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEdit = async () => {
    if (!editData) return;
    let submitData = {
      ...editData,
      conditions: adjustConditionsByMissionType(editData),
      missionDisplay: {
        ...editData.missionDisplay,
        detail: adjustMissionDisplayDetail(editData),
      },
    };
    if (env.useMock) {
      console.log("---mock---");
      console.log(submitData);
      navigate("/missions");
      return;
    }
    console.log(submitData);
    setIsSubmitting(true);
    try {
      console.log(submitData);
      const result = await editMission(paramsMissionId, submitData);
      if (result) {
        navigate("/missions");
      } else {
        alert(
          "ミッションの編集に失敗しました。ログインし直すか、設計にご連絡ください。"
        );
      }
    } catch (error) {
      alert(
        "ミッションの編集に失敗しました。ログインし直すか、設計にご連絡ください。"
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) return <p>{error}</p>;

  return (
    <div>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <div>
          <HeaderTemplate />
        </div>

        <Box
          sx={{
            paddingTop: "20px",
          }}
        >
          {editData ? (
            <MissionFormBase
              formData={editData}
              setFormData={setEditData}
              onValidationChange={setIsFormValid}
              radioDisabled={true}
            />
          ) : (
            <p>読み込み中...</p>
          )}
        </Box>
        <ActionDialogButton
          type="edit"
          open={open}
          handleClickOpen={handleClickOpen}
          handleClose={handleClose}
          handleAction={handleEdit}
          actionDisabled={!isFormValid}
          isSubmitting={isSubmitting}
        />
      </Box>
    </div>
  );
}
